/* SPEC-514 — the copilot's "Previous chats" log: pure rules, no React, no `@/` alias, so
 * `config/copilotHistory.test.ts` asserts them under Node's type stripping.
 *
 * Behaviour (Param, 07 Oct): opening the drawer starts a FRESH conversation; what was said
 * before is kept in a per-user log the reader can reopen and continue.
 *
 *  - Where it lives: this browser's localStorage, one key PER USER (`historyKey`), so a shared
 *    machine never shows A's questions to B. There is no server-side copy to reuse: the
 *    server logs model calls as meters only (llm_calls, SPEC-483), never the words, by design.
 *  - THE STORE IS THE SOURCE OF TRUTH (fix loop 1, Codex P1-4). The drawer's on-screen thread
 *    is a view of it: every write MERGES by message id into the stored conversation, never
 *    replaces it, so a reply saved by another drawer instance (the mobile drawer unmounts on
 *    close) survives a later send from a stale screen.
 *  - OWNERSHIP (Codex P1-1/2): everything the drawer shows is tagged with the user it belongs
 *    to and rendered through `drawerView`, which shows nothing that is not the current
 *    user's. A reply is saved ONLY under the user who asked (`recordReply`), and is rendered
 *    or refreshes a list only while that user is still the signed-in one (`routeReply`).
 *  - The pre-SPEC-514 single shared key holds chats with NO recorded owner. It is DISCARDED,
 *    never migrated into whoever signs in next (Codex P1-3).
 *  - Caps: MAX_CONVERSATIONS × MAX_MESSAGES, enforced on parse AND on serialise, per entry
 *    (Codex P2-6). Newest first; the oldest drop off the end.
 *  - A thread with no question in it is never logged.
 *  - A large "View data" payload is dropped from the stored copy (the answer text stays), and
 *    the whole log is trimmed oldest-first to fit a character budget.
 *  - Anything malformed in storage reads as "no history" or is normalised (`tools_used` that
 *    is not an array of strings becomes []; Codex P2-5) and never throws at render. */

export interface StoredMessage {
  /** Stable id within the conversation, used to merge copies. Filled on parse if missing. */
  id?: string;
  /** Creation order (monotonic ms; fix loop 4): tells a stale screen copy from a newer turn
   *  when the screen and the saved copy share no message. */
  seq?: number;
  role: 'user' | 'assistant';
  content: string;
  data?: unknown;
  tools_used?: string[];
}

export interface Conversation {
  id: string;
  /** ISO time the conversation started. */
  startedAt: string;
  /** ISO time of the last message. */
  updatedAt: string;
  /** The first question, trimmed — what the list shows. */
  title: string;
  messages: StoredMessage[];
}

/** The slice of the Web Storage API this module needs (a Map-backed fake in the tests). */
export interface StorageLike {
  getItem(key: string): string | null;
  setItem(key: string, value: string): void;
  removeItem(key: string): void;
}

export const MAX_CONVERSATIONS = 30;
export const MAX_MESSAGES = 50;
/** A stored "View data" payload larger than this (JSON characters) is dropped. */
export const MAX_DATA_CHARS = 4_000;
/** The whole log's budget in JSON characters (well under a browser's ~5 MB per origin). */
export const MAX_HISTORY_CHARS = 1_500_000;
export const TITLE_CHARS = 90;

/** The pre-SPEC-514 single-thread key, shared by every login on the browser. Discarded. */
export const LEGACY_THREAD_KEY = 'suryaiq_copilot_messages';

export function historyKey(username: string | null | undefined): string {
  return `suryaiq_copilot_history.v1:${username || 'anonymous'}`;
}

/* ── Ownership ────────────────────────────────────────────────────────────────────── */

/** State tagged with the log it belongs to is shown ONLY while that log is the signed-in
 *  user's; any other owner renders as empty, in the same render as the login change. */
export function ownedOrEmpty<T>(owner: string, currentKey: string, value: readonly T[]): readonly T[] {
  return owner === currentKey ? value : [];
}

/** Everything the drawer renders, each part tagged with its owner. */
export interface DrawerState {
  threadOwner: string;
  messages: readonly StoredMessage[];
  listOwner: string;
  list: readonly Conversation[];
  draftOwner: string;
  draft: string;
}

/** What the drawer may render for `userKey`: another user's thread, list or half-typed draft
 *  is never part of it. The drawer renders ONLY from this. */
export function drawerView(s: DrawerState, userKey: string): {
  messages: readonly StoredMessage[]; list: readonly Conversation[]; draft: string;
} {
  return {
    messages: ownedOrEmpty(s.threadOwner, userKey, s.messages),
    list: ownedOrEmpty(s.listOwner, userKey, s.list),
    draft: s.draftOwner === userKey ? s.draft : '',
  };
}

/** Where a reply goes once it lands. It is always SAVED under the asker (`recordReply`); it is
 *  shown, or refreshes an open list, only while the asker is still signed in and (for the
 *  thread) still looking at that conversation. */
export function routeReply(
  req: { owner: string; threadId: string },
  now: { userKey: string; threadId: string; view: 'chat' | 'history' },
): { renderInThread: boolean; refreshList: boolean } {
  const sameUser = req.owner === now.userKey;
  return {
    renderInThread: sameUser && req.threadId === now.threadId,
    refreshList: sameUser && now.view === 'history',
  };
}

/* ── Parse / serialise ────────────────────────────────────────────────────────────── */

function normaliseMessage(m: unknown, fallbackId: string): StoredMessage | null {
  if (!m || typeof m !== 'object') return null;
  const r = m as Record<string, unknown>;
  if ((r.role !== 'user' && r.role !== 'assistant') || typeof r.content !== 'string') return null;
  const out: StoredMessage = {
    id: typeof r.id === 'string' && r.id ? r.id : fallbackId,
    role: r.role,
    content: r.content,
  };
  if (typeof r.seq === 'number' && Number.isFinite(r.seq)) out.seq = r.seq;
  if (r.tools_used !== undefined) {
    const t = r.tools_used;
    out.tools_used = Array.isArray(t) && t.every((x) => typeof x === 'string') ? [...(t as string[])] : [];
  }
  if (r.data !== undefined && r.data !== null) out.data = r.data;
  return out;
}

/** Parse a stored log. Malformed → []; malformed entries are skipped; optional fields are
 *  normalised; both caps are enforced per entry. */
export function parseHistory(raw: string | null): Conversation[] {
  if (!raw) return [];
  let v: unknown;
  try {
    v = JSON.parse(raw);
  } catch {
    return [];
  }
  if (!Array.isArray(v)) return [];
  const out: Conversation[] = [];
  for (const c of v) {
    if (out.length >= MAX_CONVERSATIONS) break;
    if (!c || typeof c !== 'object') continue;
    const r = c as Record<string, unknown>;
    if (typeof r.id !== 'string' || !Array.isArray(r.messages)) continue;
    const all: StoredMessage[] = [];
    r.messages.forEach((m, i) => {
      const n = normaliseMessage(m, `${r.id}#${i}`);
      if (n) all.push(n);
    });
    if (!all.some((m) => m.role === 'user')) continue;
    out.push({
      id: r.id,
      startedAt: typeof r.startedAt === 'string' ? r.startedAt : '',
      updatedAt: typeof r.updatedAt === 'string' ? r.updatedAt : '',
      title: typeof r.title === 'string' && r.title ? r.title : titleFor(all),
      messages: all.slice(-MAX_MESSAGES),
    });
  }
  return out;
}

/** The list label: the first question, on one line, cut at TITLE_CHARS. */
export function titleFor(messages: readonly StoredMessage[]): string {
  const first = messages.find((m) => m.role === 'user');
  const text = (first?.content || '').replace(/\s+/g, ' ').trim();
  if (!text) return 'Untitled chat';
  return text.length > TITLE_CHARS ? `${text.slice(0, TITLE_CHARS - 1).trimEnd()}…` : text;
}

/** The copy of a message that goes into storage: a large data payload is dropped. */
export function storableMessage(m: StoredMessage): StoredMessage {
  const out: StoredMessage = { role: m.role, content: m.content };
  if (m.id) out.id = m.id;
  if (typeof m.seq === 'number' && Number.isFinite(m.seq)) out.seq = m.seq;
  if (Array.isArray(m.tools_used) && m.tools_used.length) out.tools_used = m.tools_used.filter((t) => typeof t === 'string');
  if (m.data !== undefined && m.data !== null) {
    let size = Infinity;
    try {
      size = JSON.stringify(m.data).length;
    } catch {
      size = Infinity;
    }
    if (size <= MAX_DATA_CHARS) out.data = m.data;
  }
  return out;
}

/** Serialise within the caps and MAX_HISTORY_CHARS, dropping the OLDEST conversations first.
 *  The newest is always kept (its messages trimmed oldest-first if it alone is over budget). */
export function serializeHistory(history: Conversation[], budget: number = MAX_HISTORY_CHARS): string {
  let list = history.slice(0, MAX_CONVERSATIONS).map((c) => ({ ...c, messages: c.messages.slice(-MAX_MESSAGES) }));
  let json = JSON.stringify(list);
  while (json.length > budget && list.length > 1) {
    list = list.slice(0, -1);
    json = JSON.stringify(list);
  }
  while (json.length > budget && list.length === 1 && list[0].messages.length > 1) {
    list = [{ ...list[0], messages: list[0].messages.slice(1) }];
    json = JSON.stringify(list);
  }
  return json;
}

/* ── Merging ──────────────────────────────────────────────────────────────────────── */

function messageKey(m: StoredMessage, i: number): string {
  return m.id ? `id:${m.id}` : `ix:${i}:${m.role}:${m.content}`;
}

/** The union of two copies of one conversation, in conversation order (fix loop 3).
 *  The SCREEN's order is the base. A message only the store holds (a reply another drawer
 *  saved) is slotted straight after its predecessor in the store, or first if it has none.
 *  A screen message the store trimmed at the cap keeps its place at the START (it is the
 *  oldest), so it can never reappear after the newest turns. The caller trims to the cap. */
export function mergeMessages(stored: readonly StoredMessage[], screen: readonly StoredMessage[]): StoredMessage[] {
  const storedKeys = stored.map(messageKey);
  const screenKeys = screen.map(messageKey);
  // Fix loop 4: a screen copy that shares NO message with the saved copy is stale (another
  // drawer carried the chat on and the cap trimmed what this one shows). The saved copy is
  // then the truth: only screen messages NEWER than its newest are added, after it.
  const storedSet = new Set(storedKeys);
  if (stored.length && screen.length && !screenKeys.some((k) => storedSet.has(k))) {
    const newest = Math.max(...stored.map((m) => (typeof m.seq === 'number' ? m.seq : -Infinity)));
    const seen = new Set<string>(storedKeys);
    const out = [...stored];
    screen.forEach((m, i) => {
      const newer = newest === -Infinity || (typeof m.seq === 'number' && m.seq > newest);
      if (newer && !seen.has(screenKeys[i])) {
        seen.add(screenKeys[i]);
        out.push(m);
      }
    });
    return out;
  }
  const out: StoredMessage[] = [];
  const outKeys: string[] = [];
  screen.forEach((m, i) => {
    if (outKeys.includes(screenKeys[i])) return;
    out.push(m);
    outKeys.push(screenKeys[i]);
  });
  stored.forEach((m, i) => {
    const k = storedKeys[i];
    if (outKeys.includes(k)) return;
    const at = i === 0 ? 0 : outKeys.indexOf(storedKeys[i - 1]) + 1;
    out.splice(at, 0, m);
    outKeys.splice(at, 0, k);
  });
  return out;
}

/* ── Sequence allocation (fix loop 5) ─────────────────────────────────────────────── */

/** The highest `seq` saved anywhere in a user's log (0 when none). */
export function maxSeq(history: readonly Conversation[]): number {
  let max = 0;
  for (const c of history) for (const m of c.messages) if (typeof m.seq === 'number' && m.seq > max) max = m.seq;
  return max;
}

/** The next sequence: after the last one issued AND the saved maximum the caller seeded into
 *  `last`, or the clock if that is later. Never goes backwards when the clock does, or after a
 *  reload (the caller seeds `last` from the saved log first). */
export function allocateSeq(last: number, now: number): number {
  const base = Number.isFinite(last) ? last : 0;
  return Math.max(base + 1, Number.isFinite(now) ? now : 0);
}

/* ── Pure log steps ───────────────────────────────────────────────────────────────── */

/** Put a thread into the log, MERGED with any stored copy of the same id, and move it to the
 *  top. A thread with no question is not logged: the log comes back unchanged. */
export function upsertConversation(
  history: Conversation[],
  thread: { id: string; startedAt: string; messages: readonly StoredMessage[] },
  nowIso: string,
): Conversation[] {
  const existing = history.find((c) => c.id === thread.id);
  const merged = mergeMessages(existing?.messages ?? [], thread.messages);
  if (!merged.some((m) => m.role === 'user')) return history;
  const entry: Conversation = {
    id: thread.id,
    startedAt: existing?.startedAt || thread.startedAt || nowIso,
    updatedAt: nowIso,
    // Title from the FULL thread, so trimming old messages never renames the chat.
    title: existing?.title || titleFor(merged),
    messages: merged.slice(-MAX_MESSAGES).map(storableMessage),
  };
  const rest = history.filter((c) => c.id !== thread.id);
  return [entry, ...rest].slice(0, MAX_CONVERSATIONS);
}

/** Add one message to a logged conversation. Unknown id → unchanged. */
export function appendToConversation(
  history: Conversation[],
  id: string,
  message: StoredMessage,
  nowIso: string,
): Conversation[] {
  const found = history.find((c) => c.id === id);
  if (!found) return history;
  return upsertConversation(history, { id, startedAt: found.startedAt, messages: [message] }, nowIso);
}

export function removeConversation(history: Conversation[], id: string): Conversation[] {
  return history.filter((c) => c.id !== id);
}

/* ── The store (what the drawer calls) ────────────────────────────────────────────── */

export function loadHistory(store: StorageLike, key: string): Conversation[] {
  try {
    return parseHistory(store.getItem(key));
  } catch {
    return [];
  }
}

/** Read the stored log, apply one pure step, write it back. Never a stale in-memory copy:
 *  two drawer instances share the key. */
export function saveHistory(
  store: StorageLike,
  key: string,
  step: (h: Conversation[]) => Conversation[],
): { list: Conversation[]; saved: boolean } {
  const next = step(loadHistory(store, key));
  try {
    store.setItem(key, serializeHistory(next));
    return { list: next, saved: true };
  } catch {
    return { list: next, saved: false };
  }
}

/** Save a reply under the user who ASKED, merged into their conversation by id. */
export function recordReply(
  store: StorageLike,
  req: { owner: string; threadId: string },
  reply: StoredMessage,
  nowIso: string,
): Conversation[] {
  return saveHistory(store, req.owner, (h) => appendToConversation(h, req.threadId, reply, nowIso)).list;
}

/** A conversation to open, read from THIS user's log at the moment of opening. Not in it →
 *  null (never a snapshot from a list, which may be stale or another user's). */
export function openFromStore(store: StorageLike, userKey: string, id: string): Conversation | null {
  return loadHistory(store, userKey).find((c) => c.id === id) ?? null;
}

/** The messages a send continues from: the stored copy merged with the screen, so a reply
 *  another drawer instance saved is part of the context and is never overwritten. */
export function threadForSend(
  store: StorageLike,
  thread: { owner: string; id: string; messages: readonly StoredMessage[] },
): StoredMessage[] {
  const stored = loadHistory(store, thread.owner).find((c) => c.id === thread.id);
  return mergeMessages(stored?.messages ?? [], thread.messages);
}

/** The pre-SPEC-514 shared key has no owner: delete it, never migrate it. */
export function discardLegacyThread(store: StorageLike): void {
  try {
    store.removeItem(LEGACY_THREAD_KEY);
  } catch { /* storage unavailable */ }
}

/** "Today 14:05", "Yesterday 09:12", "3 Oct 16:40", "3 Oct 2025" — for the list. */
export function formatWhen(iso: string, now: Date): string {
  const d = new Date(iso);
  if (!iso || Number.isNaN(d.getTime())) return '';
  const pad = (n: number) => String(n).padStart(2, '0');
  const time = `${pad(d.getHours())}:${pad(d.getMinutes())}`;
  const sameDay = (a: Date, b: Date) =>
    a.getFullYear() === b.getFullYear() && a.getMonth() === b.getMonth() && a.getDate() === b.getDate();
  if (sameDay(d, now)) return `Today ${time}`;
  const y = new Date(now);
  y.setDate(now.getDate() - 1);
  if (sameDay(d, y)) return `Yesterday ${time}`;
  const month = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'][d.getMonth()];
  if (d.getFullYear() === now.getFullYear()) return `${d.getDate()} ${month} ${time}`;
  return `${d.getDate()} ${month} ${d.getFullYear()}`;
}
